import { z } from 'zod';
import {
  CASAS_CUSTO,
  CASAS_DINHEIRO,
  CASAS_QUANTIDADE,
  compararDinheiro,
  compararQuantidade,
  desescalar,
  dividirArredondando,
  escalar,
  somarDinheiro,
  somarQuantidade,
} from './aritmetica.ts';
import {
  custoUnitarioSchema,
  dataSchema,
  dinheiroSchema,
  quantidadeSchema,
  tipoInsumoSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Relatório de consumo de insumos na obra (`CTR·05`).
 *
 * O que cada obra consumiu — material **e** serviço —, com código, nome,
 * quantidade, valor unitário médio e valor total. É o primeiro relatório do
 * sistema: o painel de obras (`CTR·03`) responde "quanto" de relance, e este
 * responde "de quê", linha a linha, para imprimir e levar à reunião de obra.
 *
 * ## De onde vem cada linha — as duas fontes, e por que só elas
 *
 * O custo apropriado à obra tem três parcelas no painel (`.spec/015`, decisão
 * 60): o consumo de material, o serviço comprado por nota e o título avulso. As
 * duas primeiras têm **insumo**; o título avulso — contrato de pagamento, mão de
 * obra, lançamento manual — não tem, e não cabe num relatório de insumos. Ficam,
 * então, exatamente as duas:
 *
 * - **`CONSUMO`** — os itens das requisições de consumo (`lancamentos_consumo_itens`),
 *   inclusive as geradas pelo consumo automático da nota. Valor é quantidade ×
 *   custo médio do instante da baixa, e não se reprecifica depois.
 * - **`NOTA`** — a fatia rateada à obra dos itens de nota **confirmada** cuja
 *   operação fiscal **não movimenta estoque**: subempreitada, serviço, e o
 *   material que foi direto para o canteiro sem passar por depósito. É o mesmo
 *   recorte de `somaDoServicoDeNota`, no repositório do painel, e pelo mesmo
 *   motivo: o item que movimenta estoque volta como consumo quando é aplicado, e
 *   contá-lo também na nota daria à obra o dobro do cimento.
 *
 * E uma terceira, que **abate**: a **`DEVOLUCAO`** de consumo (decisão 82, 26/09/2026)
 * — o material que voltou da obra para um depósito, com quantidade e valor
 * **negativos**, na data em que voltou. Por insumo, ela se soma à linha do
 * consumo do mesmo material e o que sobra é o que a obra usou; por data, é
 * linha própria, e o crédito aparece com o sinal de menos.
 *
 * O **tipo** da linha (material ou serviço) vem do cadastro do insumo, e não da
 * fonte: as duas coisas não coincidem sempre — uma locação de betoneira pode
 * entrar como material por uma operação que não movimenta estoque —, e o que o
 * usuário pediu para separar é a natureza do insumo, não o caminho do papel.
 *
 * ## O que se agrega onde
 *
 * O servidor devolve as linhas **já agregadas** pelo agrupamento pedido — por
 * insumo, ou por data e documento —, e o cliente só ordena e esconde colunas.
 * Ordenar e esconder valor não trocam o que se soma, então não justificam uma ida
 * ao servidor a cada clique; trocar o agrupamento troca, e é parâmetro da rota.
 */

/* -------------------------------------------------------------------------- */
/* Opções                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Como as linhas de cada obra se agrupam.
 *
 * - `INSUMO` — uma linha por insumo na obra, somando o período inteiro e as duas
 *   fontes. É a pergunta "quanto de cimento esta obra levou".
 * - `DATA` — uma linha por dia, documento e insumo. É a pergunta "quando saiu, e
 *   em que requisição", que é a que se faz ao conferir contra o papel assinado.
 */
export const AGRUPAMENTOS_CONSUMO_OBRA = ['INSUMO', 'DATA'] as const;
export const agrupamentoConsumoObraSchema = z.enum(AGRUPAMENTOS_CONSUMO_OBRA);
export type AgrupamentoConsumoObra = z.infer<typeof agrupamentoConsumoObraSchema>;

export const ROTULO_AGRUPAMENTO_CONSUMO_OBRA: Readonly<Record<AgrupamentoConsumoObra, string>> = {
  INSUMO: 'Agrupar por insumo',
  DATA: 'Exibir por data',
};

/** De que documento a linha veio. Só existe no agrupamento por data. */
export const ORIGENS_CONSUMO_OBRA = ['CONSUMO', 'NOTA', 'DEVOLUCAO'] as const;
export const origemConsumoObraSchema = z.enum(ORIGENS_CONSUMO_OBRA);
export type OrigemConsumoObra = z.infer<typeof origemConsumoObraSchema>;

export const ROTULO_ORIGEM_CONSUMO_OBRA: Readonly<Record<OrigemConsumoObra, string>> = {
  CONSUMO: 'Consumo de obra',
  NOTA: 'Nota de compra',
  DEVOLUCAO: 'Devolução de consumo',
};

/**
 * Teto de linhas por consulta.
 *
 * O relatório não é paginado: os totais por obra e o PDF precisam do conjunto
 * inteiro, e uma página 2 imprimiria uma obra pela metade. O teto recusa em vez
 * de truncar — relatório cortado em silêncio soma menos do que a obra gastou, e
 * ninguém percebe. Só o agrupamento por data chega perto dele; por insumo, o
 * número de linhas é limitado pelo catálogo.
 */
export const LIMITE_LINHAS_CONSUMO_OBRA = 5000;

/* -------------------------------------------------------------------------- */
/* Consulta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros do relatório. **Todos opcionais**, e a ausência é "sem recorte".
 *
 * `empresaId` é a empresa **da obra** (`obras.empresa_id`), como no painel de
 * obras — e não a do cabeçalho do documento. As duas coincidem por regra (a obra
 * de um documento só pode ser da empresa dele), mas a pergunta aqui é sobre
 * canteiros, e é pela obra que se recorta.
 *
 * O período filtra a **data do fato** de cada fonte: `data_lancamento` do
 * consumo, que é o dia em que o material saiu do depósito, e `data_lancamento`
 * da nota, que é a mesma data que o consumo automático dela recebe. Inclusivo
 * nas duas pontas.
 *
 * Estrito: parâmetro desconhecido é erro, e não silêncio (ver `query-estrita`).
 */
export const relatorioConsumoObraQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    obraId: uuidSchema.optional(),
    de: dataSchema.optional(),
    ate: dataSchema.optional(),
    agrupamento: agrupamentoConsumoObraSchema.prefault('INSUMO'),
  })
  .strict();

export type RelatorioConsumoObraQuery = z.infer<typeof relatorioConsumoObraQuerySchema>;
/** O que o cliente manda: o agrupamento pode faltar, e cai no padrão. */
export type RelatorioConsumoObraQueryEntrada = z.input<typeof relatorioConsumoObraQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Resposta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Uma linha do relatório.
 *
 * `data`, `origem` e `documento` só vêm no agrupamento por data; por insumo são
 * `null`, porque a linha soma dias e documentos diferentes e qualquer um deles
 * escrito ali seria dado inventado.
 *
 * `valorUnitario` é **médio**: `valorTotal ÷ quantidade`, com as seis casas do
 * custo unitário. Não é o custo médio do depósito nem o preço da nota — é o que
 * a obra pagou, em média, por unidade daquilo que recebeu. Seis casas porque é
 * taxa, e cortá-la em duas faria o unitário não multiplicar de volta ao total.
 */
export const linhaConsumoObraSchema = z.object({
  insumoId: uuidSchema,
  insumoCodigo: z.string(),
  insumoNome: z.string(),
  insumoTipo: tipoInsumoSchema,
  unidadeSigla: z.string(),
  /** Decide se a quantidade se exibe com decimais. */
  unidadeApenasInteiros: z.boolean(),
  data: dataSchema.nullable(),
  origem: origemConsumoObraSchema.nullable(),
  /** "Consumo 42", "NF 1234/1", "Devolução 7". */
  documento: z.string().nullable(),
  quantidade: quantidadeSchema,
  valorUnitario: custoUnitarioSchema,
  valorTotal: dinheiroSchema,
});

export type LinhaConsumoObra = z.infer<typeof linhaConsumoObraSchema>;

/**
 * Os totais de um bloco — uma obra, ou o relatório inteiro.
 *
 * Material e serviço **separados**, e o total ao lado: é a divisão que o pedido
 * do relatório nomeou, e é a que o engenheiro compara com o orçamento, que também
 * separa os dois.
 *
 * Quantidade não se totaliza: doze sacos mais três horas de guindaste não somam
 * quinze de nada.
 */
export const totaisConsumoObraSchema = z.object({
  material: dinheiroSchema,
  servico: dinheiroSchema,
  total: dinheiroSchema,
  linhas: z.number().int().nonnegative(),
});

export type TotaisConsumoObra = z.infer<typeof totaisConsumoObraSchema>;

export const obraDoConsumoSchema = z.object({
  obraId: uuidSchema,
  obraCodigo: z.string().nullable(),
  obraNome: z.string(),
  empresaId: uuidSchema,
  empresaNome: z.string(),
  linhas: z.array(linhaConsumoObraSchema),
  totais: totaisConsumoObraSchema,
});

export type ObraDoConsumo = z.infer<typeof obraDoConsumoSchema>;

/**
 * O relatório inteiro.
 *
 * O filtro volta resolvido — os nomes da empresa e da obra escolhidas — porque a
 * exportação imprime o cabeçalho, e resolver os nomes no cliente custaria duas
 * consultas a mais só para montar o PDF. É a mesma escolha do cardex.
 *
 * Só aparecem as obras **com consumo** no recorte: um relatório de consumo com
 * vinte obras paradas listadas em branco é ruído, e a obra que se procura e não
 * aparece já diz o que precisa — não consumiu nada no período.
 */
export const relatorioConsumoObraSchema = z.object({
  agrupamento: agrupamentoConsumoObraSchema,
  empresaId: uuidSchema.nullable(),
  empresaNome: z.string().nullable(),
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  de: dataSchema.nullable(),
  ate: dataSchema.nullable(),
  obras: z.array(obraDoConsumoSchema),
  totais: totaisConsumoObraSchema,
});

export type RelatorioConsumoObra = z.infer<typeof relatorioConsumoObraSchema>;

/* -------------------------------------------------------------------------- */
/* Montagem — do que o banco agregou ao relatório                             */
/* -------------------------------------------------------------------------- */

/**
 * Uma linha como o repositório a devolve: já somada pela fonte, com a obra
 * ao lado.
 *
 * As duas fontes chegam separadas porque vêm de tabelas diferentes, e no
 * agrupamento por insumo o mesmo material pode estar nas duas — o cimento
 * consumido do depósito e o que foi direto da nota para a obra. Juntá-las é o
 * trabalho de `montarRelatorioConsumoObra`.
 */
export interface LinhaBrutaConsumoObra {
  obraId: string;
  obraCodigo: string | null;
  obraNome: string;
  empresaId: string;
  empresaNome: string;
  insumoId: string;
  insumoCodigo: string;
  insumoNome: string;
  insumoTipo: z.infer<typeof tipoInsumoSchema>;
  unidadeSigla: string;
  unidadeApenasInteiros: boolean;
  data: string | null;
  origem: OrigemConsumoObra | null;
  documento: string | null;
  quantidade: string;
  valorTotal: string;
}

/**
 * O valor unitário médio: total ÷ quantidade, com seis casas.
 *
 * Aritmética inteira (`BigInt`), como o resto do contrato: `Number` erraria a
 * sexta casa, e é ela que faz o unitário multiplicar de volta ao total.
 *
 * Quantidade zero devolve zero, e não erro: acontece por insumo quando a obra
 * devolveu tudo o que consumiu, e uma exceção aqui derrubaria o relatório
 * inteiro por uma linha.
 */
export function valorUnitarioMedioDoConsumo(valorTotal: string, quantidade: string): string {
  /*
    A linha de devolução chega com quantidade e valor **negativos**, e o
    unitário dela é positivo — é o custo por unidade do que voltou. O sinal sai
    da quantidade para o dividendo antes da conta porque `dividirArredondando`
    arredonda sobre o valor absoluto do dividendo e espera divisor positivo.
  */
  const bruto = escalar(quantidade, CASAS_QUANTIDADE);
  const negativo = bruto < 0n;
  const divisor = negativo ? -bruto : bruto;
  if (divisor === 0n) return desescalar(0n, CASAS_CUSTO);

  // valor com 2 casas ÷ quantidade com 4 → unitário com 6:
  // (v·10²)·10⁸ ÷ (q·10⁴) = (v÷q)·10⁶.
  const valor = escalar(valorTotal, CASAS_DINHEIRO);
  const dividendo =
    (negativo ? -valor : valor) * 10n ** BigInt(CASAS_CUSTO + CASAS_QUANTIDADE - CASAS_DINHEIRO);

  return desescalar(dividirArredondando(dividendo, divisor), CASAS_CUSTO);
}

/**
 * A chave que identifica uma linha dentro da obra.
 *
 * Por insumo, só o insumo — e é isso que junta as duas fontes. Por data, o dia,
 * a origem e o documento entram também: a mesma requisição com o mesmo cimento em
 * duas etapas vira uma linha só (a etapa não é coluna deste relatório), mas duas
 * requisições do mesmo dia continuam separadas, porque é por documento que se
 * confere.
 */
function chaveDaLinha(linha: LinhaBrutaConsumoObra, agrupamento: AgrupamentoConsumoObra): string {
  if (agrupamento === 'INSUMO') return linha.insumoId;
  return [linha.data ?? '', linha.origem ?? '', linha.documento ?? '', linha.insumoId].join('|');
}

function totaisDe(linhas: readonly LinhaConsumoObra[]): TotaisConsumoObra {
  const doTipo = (tipo: LinhaConsumoObra['insumoTipo']): string =>
    somarDinheiro(linhas.filter((l) => l.insumoTipo === tipo).map((l) => l.valorTotal));

  return {
    material: doTipo('MATERIAL'),
    servico: doTipo('SERVICO'),
    total: somarDinheiro(linhas.map((l) => l.valorTotal)),
    linhas: linhas.length,
  };
}

interface Acumulada {
  base: LinhaBrutaConsumoObra;
  quantidades: string[];
  valores: string[];
}

function acumularNaObra(
  obra: Map<string, Acumulada>,
  bruta: LinhaBrutaConsumoObra,
  agrupamento: AgrupamentoConsumoObra,
): void {
  const chave = chaveDaLinha(bruta, agrupamento);
  const existente = obra.get(chave);

  if (existente) {
    existente.quantidades.push(bruta.quantidade);
    existente.valores.push(bruta.valorTotal);
    return;
  }
  obra.set(chave, { base: bruta, quantidades: [bruta.quantidade], valores: [bruta.valorTotal] });
}

function paraLinha(
  { base, quantidades, valores }: Acumulada,
  agrupamento: AgrupamentoConsumoObra,
): LinhaConsumoObra {
  const quantidade = somarQuantidade(quantidades);
  const valorTotal = somarDinheiro(valores);
  const porData = agrupamento === 'DATA';

  return {
    insumoId: base.insumoId,
    insumoCodigo: base.insumoCodigo,
    insumoNome: base.insumoNome,
    insumoTipo: base.insumoTipo,
    unidadeSigla: base.unidadeSigla,
    unidadeApenasInteiros: base.unidadeApenasInteiros,
    data: porData ? base.data : null,
    origem: porData ? base.origem : null,
    documento: porData ? base.documento : null,
    quantidade,
    valorUnitario: valorUnitarioMedioDoConsumo(valorTotal, quantidade),
    valorTotal,
  };
}

/**
 * A linha que a devolução zerou por inteiro — o material voltou todo — sai do
 * relatório: "Cimento · 0 SC · R$ 0,00" não diz nada que a ausência não diga, e
 * lista como consumo o que a obra não usou. Só acontece por insumo; por data, a
 * devolução é linha própria e nunca soma com o consumo.
 */
function temMovimento(linha: LinhaConsumoObra): boolean {
  return (
    compararQuantidade(linha.quantidade, '0') !== 0 || compararDinheiro(linha.valorTotal, '0') !== 0
  );
}

const COMPARAR_TEXTO = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

/**
 * Junta as linhas das fontes em obras, soma o que tem a mesma chave e fecha
 * os totais.
 *
 * Vive no contrato, e não no service, porque é regra pura e testável sem banco:
 * a soma que junta as fontes é o ponto em que o relatório pode contar o mesmo
 * cimento duas vezes ou nenhuma, e é aí que o teste precisa estar.
 *
 * As obras saem em ordem alfabética; as linhas, na ordem padrão do agrupamento
 * (`ordenarLinhasConsumoObra` com `NOME` ou `DATA`). A tela reordena por cima.
 */
export function montarRelatorioConsumoObra(
  brutas: readonly LinhaBrutaConsumoObra[],
  agrupamento: AgrupamentoConsumoObra,
): { obras: ObraDoConsumo[]; totais: TotaisConsumoObra } {
  const porObra = new Map<
    string,
    { cabecalho: LinhaBrutaConsumoObra; linhas: Map<string, Acumulada> }
  >();

  for (const bruta of brutas) {
    let obra = porObra.get(bruta.obraId);
    if (!obra) {
      obra = { cabecalho: bruta, linhas: new Map() };
      porObra.set(bruta.obraId, obra);
    }
    acumularNaObra(obra.linhas, bruta, agrupamento);
  }

  const ordemPadrao: OrdemConsumoObra = agrupamento === 'DATA' ? 'DATA' : 'NOME';

  const obras = [...porObra.values()]
    .map(({ cabecalho, linhas }): ObraDoConsumo => {
      const prontas = ordenarLinhasConsumoObra(
        [...linhas.values()]
          .map((acumulada) => paraLinha(acumulada, agrupamento))
          .filter(temMovimento),
        ordemPadrao,
      );
      return {
        obraId: cabecalho.obraId,
        obraCodigo: cabecalho.obraCodigo,
        obraNome: cabecalho.obraNome,
        empresaId: cabecalho.empresaId,
        empresaNome: cabecalho.empresaNome,
        linhas: prontas,
        totais: totaisDe(prontas),
      };
    })
    // A obra que devolveu tudo o que consumiu fica sem linha, e sai como a obra
    // sem consumo sai: não consumiu nada no período.
    .filter((obra) => obra.linhas.length > 0)
    .sort((a, b) => COMPARAR_TEXTO.compare(a.obraNome, b.obraNome));

  return { obras, totais: totaisDe(obras.flatMap((obra) => obra.linhas)) };
}

/* -------------------------------------------------------------------------- */
/* Ordenação — o que a tela troca sem ir ao servidor                          */
/* -------------------------------------------------------------------------- */

/**
 * As ordens possíveis dentro de cada obra.
 *
 * Valor e quantidade são **do maior para o menor** — é a pergunta "onde foi o
 * dinheiro", e ninguém a faz de baixo para cima. Nome é alfabético, e data é
 * cronológica, que é a ordem em que se confere contra a pasta de requisições.
 */
export const ORDENS_CONSUMO_OBRA = ['NOME', 'VALOR', 'QUANTIDADE', 'DATA'] as const;
export type OrdemConsumoObra = (typeof ORDENS_CONSUMO_OBRA)[number];

export const ROTULO_ORDEM_CONSUMO_OBRA: Readonly<Record<OrdemConsumoObra, string>> = {
  NOME: 'Nome do insumo',
  VALOR: 'Valor (maior para o menor)',
  QUANTIDADE: 'Quantidade (maior para o menor)',
  DATA: 'Data',
};

/**
 * Quais ordens fazem sentido com as opções escolhidas.
 *
 * Valor só com o valor à vista — ordenar por uma coluna escondida produz uma
 * lista que parece embaralhada. Data só no agrupamento por data, porque por
 * insumo a linha soma dias diferentes e não tem data.
 */
export function ordensDisponiveisConsumoObra(opcoes: {
  agrupamento: AgrupamentoConsumoObra;
  exibirValor: boolean;
}): OrdemConsumoObra[] {
  return ORDENS_CONSUMO_OBRA.filter((ordem) => {
    if (ordem === 'VALOR') return opcoes.exibirValor;
    if (ordem === 'DATA') return opcoes.agrupamento === 'DATA';
    return true;
  });
}

/**
 * A ordem escolhida, ou a padrão quando ela deixou de valer.
 *
 * Quem esconde o valor com a lista ordenada por valor não deve ver a lista
 * mudar de ordem sem motivo aparente nem ficar com um seletor apontando para uma
 * opção que sumiu — a ordem cai no nome, que é a que sempre existe.
 */
export function ordemEfetivaConsumoObra(
  escolhida: OrdemConsumoObra,
  opcoes: { agrupamento: AgrupamentoConsumoObra; exibirValor: boolean },
): OrdemConsumoObra {
  return ordensDisponiveisConsumoObra(opcoes).includes(escolhida) ? escolhida : 'NOME';
}

/**
 * Ordena as linhas de uma obra. Não altera o vetor recebido.
 *
 * O desempate é sempre o nome e, depois dele, o documento: dois insumos com o
 * mesmo valor não podem trocar de lugar entre uma renderização e outra, e o PDF
 * precisa sair na mesma ordem da tela.
 *
 * Quantidades de unidades diferentes se comparam pelo número, e é o que o pedido
 * do relatório quer: é uma ordem de leitura, não uma soma.
 */
export function ordenarLinhasConsumoObra(
  linhas: readonly LinhaConsumoObra[],
  ordem: OrdemConsumoObra,
): LinhaConsumoObra[] {
  const porNome = (a: LinhaConsumoObra, b: LinhaConsumoObra): number =>
    COMPARAR_TEXTO.compare(a.insumoNome, b.insumoNome) ||
    COMPARAR_TEXTO.compare(a.documento ?? '', b.documento ?? '');

  const principal: Record<OrdemConsumoObra, (a: LinhaConsumoObra, b: LinhaConsumoObra) => number> =
    {
      NOME: () => 0,
      VALOR: (a, b) => compararDinheiro(b.valorTotal, a.valorTotal),
      QUANTIDADE: (a, b) => compararQuantidade(b.quantidade, a.quantidade),
      DATA: (a, b) => (a.data ?? '').localeCompare(b.data ?? ''),
    };

  return [...linhas].sort((a, b) => principal[ordem](a, b) || porNome(a, b));
}
